import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { getReq } from 'api';
import {
    coveragesSelector,
    fetchRespondedAlerts,
    updateCoverage,
} from 'store/company/coverage';
import { handleHideSidescreen } from 'store/ux';
import styles from 'styles/modules/SidescreenAddMediaC.module.scss';
import Details from './addMediaCoverage/Details';
import Description from './addMediaCoverage/Description';
import SidescreenHeader from './SidescreenHeader';
import OptionSelect from 'components/OptionSelect';
import Divider from 'components/Divider';
import Button from 'components/Button';
import Typo from 'components/Typo';
import { profile } from 'lang/en.json';

const text = profile.media_coverage.edit;

const EditMediaCoverage = () => {
    const dispatch = useDispatch();
    const { editing, respondedAlerts } = useSelector(coveragesSelector);
    const { register, handleSubmit, errors } = useForm();
    const [details, setDetails] = useState({
        title: editing?.title || '',
        source: editing?.publisher || '',
        url: editing?.url || '',
    });
    // The list leaves the excerpt out of the page source, so it is read on its own.
    // Until it arrives it is not sent, and the API keeps what is stored.
    const [description, setDescription] = useState(undefined);
    const [alert, setAlert] = useState(
        editing?.alert
            ? { id: editing.alert.id, value: editing.alert.title }
            : null
    );
    const [applyToAll, setApplyToAll] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        dispatch(fetchRespondedAlerts());
    }, []);

    useEffect(() => {
        if (!editing) return;
        getReq(`/coverages/${editing.id}`)
            .then((res) => setDescription(res.data.data?.description ?? ''))
            .catch(() => setDescription(undefined));
    }, [editing?.id]);

    if (!editing) return null;

    const alertOptions = [
        ...(editing.alert ? [editing.alert] : []),
        ...(respondedAlerts || []).filter(({ id }) => id !== editing.alert?.id),
    ]
        .filter(({ id }) => id !== alert?.id)
        .map(({ id, title }) => ({ id, name: title }));

    const onSubmit = async () => {
        setSaving(true);
        setError(null);
        const result = await dispatch(
            updateCoverage({
                id: editing.id,
                title: details.title,
                publisher: details.source,
                url: details.url,
                ...(description !== undefined ? { description } : {}),
                alertId: alert?.id ?? null,
                applyToAll,
            })
        );
        setSaving(false);
        if (updateCoverage.rejected.match(result)) {
            setError(result.payload?.message || text.error);
            return;
        }
        dispatch(handleHideSidescreen());
    };

    return (
        <Fragment>
            <SidescreenHeader title={text.title} />

            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Details
                    callback={setDetails}
                    register={register}
                    errors={errors}
                    data={details}
                />
                {description !== undefined && (
                    <Description
                        callback={setDescription}
                        description={description}
                    />
                )}
                <div className={['mb-m', styles.alertPicker].join(' ')}>
                    <Typo variant={'small'} className={'mb-xs'}>
                        {profile.media_coverage.sidebar.coverage_alert}
                    </Typo>
                    <OptionSelect
                        options={alertOptions}
                        value={{ value: alert?.value || text.no_alert }}
                        defaultValue={alert ? text.no_alert : undefined}
                        setValue={({ id, value }) =>
                            setAlert(id ? { id, value } : null)
                        }
                    />
                </div>
                <label className={['mb-m', styles.applyToAll].join(' ')}>
                    <input
                        type={'checkbox'}
                        checked={applyToAll}
                        onChange={(e) => setApplyToAll(e.target.checked)}
                    />
                    <Typo variant={'small'}>{text.apply_to_all}</Typo>
                </label>
                <Typo variant={'small'} className={'mb-m'}>
                    {text.no_email}
                </Typo>
                {error && (
                    <Typo variant={'small'} className={styles.alertLinkError}>
                        {error}
                    </Typo>
                )}
                <Divider />
                <Button
                    title={text.save}
                    loading={saving}
                    sticky={{ position: 'bottom' }}
                />
            </form>
        </Fragment>
    );
};

export default EditMediaCoverage;
